'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import { AlertCircle, CheckCircle2, Info, AlertTriangle, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Unterstuetzte Nachrichtentypen fuer System-Nachrichten in der UI.
 * Unbekannte Typen werden auf "info" gemappt (Fallback).
 */
export type ToastVariant = 'error' | 'success' | 'info' | 'warning'

const KNOWN_VARIANTS: ReadonlyArray<ToastVariant> = ['error', 'success', 'info', 'warning']

/** Standard-Anzeigedauer in Millisekunden fuer Auto-Dismiss. */
export const DEFAULT_TOAST_DURATION_MS = 5000

export interface ToastOptions {
  /** Optionaler Titel (fett dargestellt). */
  title?: string
  /** Nachrichttext; Pflichtfeld. */
  message: string
  /** Typ der Nachricht (Error, Success, Info, Warning). Fallback: info. */
  variant?: ToastVariant
  /**
   * Dauer in Millisekunden bis zum automatischen Schliessen.
   * `null` oder `0` deaktiviert Auto-Dismiss (nur manuell schliessbar).
   */
  durationMs?: number | null
}

export interface Toast extends Required<Omit<ToastOptions, 'title' | 'durationMs'>> {
  id: string
  title?: string
  durationMs: number | null
}

interface ToastContextValue {
  toasts: Toast[]
  show: (options: ToastOptions) => string
  error: (message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) => string
  success: (message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) => string
  info: (message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) => string
  warning: (message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) => string
  dismiss: (id: string) => void
  dismissAll: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

/**
 * Normalisiert die Variant auf einen bekannten Wert.
 * Negativfall (unbekannter Typ) wird auf "info" gemappt.
 */
export function normalizeVariant(variant: unknown): ToastVariant {
  if (typeof variant === 'string' && (KNOWN_VARIANTS as ReadonlyArray<string>).includes(variant)) {
    return variant as ToastVariant
  }
  return 'info'
}

function generateId(): string {
  return crypto.randomUUID()
}

interface ToastProviderProps {
  children: ReactNode
  /** Maximale Anzahl gleichzeitig sichtbarer Toasts; FIFO. */
  limit?: number
}

export function ToastProvider({ children, limit = 5 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const clearTimer = useCallback((id: string) => {
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id)
      setToasts((prev) => prev.filter((t) => t.id !== id))
    },
    [clearTimer],
  )

  const dismissAll = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer))
    timersRef.current.clear()
    setToasts([])
  }, [])

  const show = useCallback(
    (options: ToastOptions): string => {
      const id = generateId()
      const variant = normalizeVariant(options.variant)
      const durationMs =
        options.durationMs === null || options.durationMs === 0
          ? null
          : typeof options.durationMs === 'number'
            ? options.durationMs
            : DEFAULT_TOAST_DURATION_MS

      const toast: Toast = {
        id,
        message: options.message,
        variant,
        durationMs,
        ...(options.title !== undefined ? { title: options.title } : {}),
      }

      setToasts((prev) => {
        const next = [...prev, toast]
        // FIFO begrenzen
        while (next.length > limit) {
          const removed = next.shift()
          if (removed) clearTimer(removed.id)
        }
        return next
      })

      if (durationMs !== null && durationMs > 0) {
        const timer = setTimeout(() => dismiss(id), durationMs)
        timersRef.current.set(id, timer)
      }

      return id
    },
    [dismiss, clearTimer, limit],
  )

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      show,
      error: (message, options) => show({ ...(options ?? {}), message, variant: 'error' }),
      success: (message, options) => show({ ...(options ?? {}), message, variant: 'success' }),
      info: (message, options) => show({ ...(options ?? {}), message, variant: 'info' }),
      warning: (message, options) => show({ ...(options ?? {}), message, variant: 'warning' }),
      dismiss,
      dismissAll,
    }),
    [toasts, show, dismiss, dismissAll],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

/**
 * Hook fuer den Zugriff auf den Toast-Service.
 * Wirft einen Fehler, wenn ausserhalb des ToastProviders aufgerufen.
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast muss innerhalb eines ToastProviders verwendet werden.')
  }
  return context
}

interface VariantStyle {
  container: string
  iconWrap: string
  Icon: LucideIcon
  /** Live-Region-Prioritaet: assertive fuer Error, polite fuer den Rest. */
  ariaLive: 'assertive' | 'polite'
  /** Rolle: alert fuer Fehler, status fuer den Rest. */
  role: 'alert' | 'status'
  /** Lesbare Bezeichnung des Typs fuer Screenreader. */
  srLabel: string
}

const VARIANT_STYLES: Record<ToastVariant, VariantStyle> = {
  error: {
    container:
      'border-red-200 bg-red-50 text-red-800 ring-red-200 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200',
    iconWrap: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300',
    Icon: AlertCircle,
    ariaLive: 'assertive',
    role: 'alert',
    srLabel: 'Fehler',
  },
  success: {
    container:
      'border-emerald-200 bg-emerald-50 text-emerald-800 ring-emerald-200 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200',
    iconWrap: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300',
    Icon: CheckCircle2,
    ariaLive: 'polite',
    role: 'status',
    srLabel: 'Erfolg',
  },
  info: {
    container:
      'border-primary-200 bg-primary-50 text-primary-800 ring-primary-200 dark:border-primary-900/40 dark:bg-primary-950/40 dark:text-primary-200',
    iconWrap: 'bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300',
    Icon: Info,
    ariaLive: 'polite',
    role: 'status',
    srLabel: 'Hinweis',
  },
  warning: {
    container:
      'border-amber-200 bg-amber-50 text-amber-800 ring-amber-200 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200',
    iconWrap: 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300',
    Icon: AlertTriangle,
    ariaLive: 'polite',
    role: 'status',
    srLabel: 'Warnung',
  },
}

interface ToastViewportProps {
  toasts: Toast[]
  onDismiss: (id: string) => void
}

function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  const tc = useTranslations('common')
  if (toasts.length === 0) return null

  return (
    <div
      aria-label={tc('ariaSystemMessages')}
      className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-4 sm:top-4 sm:items-end"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

interface ToastItemProps {
  toast: Toast
  onDismiss: (id: string) => void
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const tc = useTranslations('common')
  const style = VARIANT_STYLES[toast.variant]
  const { Icon } = style

  return (
    <div
      data-testid={`toast-${toast.variant}`}
      role={style.role}
      aria-live={style.ariaLive}
      aria-atomic="true"
      className={cn(
        'pointer-events-auto relative flex w-full max-w-sm items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-card ring-1 ring-inset',
        style.container,
      )}
    >
      <div
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          style.iconWrap,
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only">{style.srLabel}:</span>
      </div>
      <div className="min-w-0 flex-1">
        {toast.title ? (
          <p className="break-words font-semibold leading-5">{toast.title}</p>
        ) : null}
        <p className={cn('break-words leading-5', toast.title ? 'mt-0.5 text-[0.8125rem] opacity-90' : '')}>
          {toast.message}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-current opacity-70 transition hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
        aria-label={tc('ariaCloseMessage')}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}
